import type {
  OutreachType as UrlOutreachType,
  EmployerOutreachData,
} from "./useEmployerOutreachSource";
import type { ResumeEngagement } from "../components/core/JobComponent/EmployerOutreach/ResumeEngagementDataBuilder";

import { type OutreachInfo, OutreachStatus, OutreachType } from "./useEmployerOutreachUIConfig";

/**
 * Maps engagement status string to OutreachStatus enum
 */
const mapEngagementStatus = (status: string): OutreachStatus | null => {
  const upperStatus = status.toUpperCase();
  switch (upperStatus) {
    case "SENT":
    case "INITIATED":
      return OutreachStatus.INITIATED;
    case "INTERESTED":
      return OutreachStatus.INTERESTED;
    case "DECLINED":
      return OutreachStatus.DECLINED;
    default:
      return null;
  }
};

/**
 * Maps URL outreach type to OutreachType enum
 */
const mapOutreachType = (urlOutreachType: UrlOutreachType): OutreachType | null => {
  if (!urlOutreachType) {
    return null;
  }

  // Fast apply was deprecated.
  // For non-custom jobs, all outreaches are now FULL_APPLY.
  // Keeping this code because we have a plan to reuse this for Sourcing Jobs.
  return OutreachType.FULL_APPLY;
};

/**
 * Creates OutreachInfo from engagement data and URL params
 */
export const mapToOutreachInfo = (
  engagement: ResumeEngagement | null,
  employerOutreachData: EmployerOutreachData,
): OutreachInfo | null => {
  if (!engagement) {
    return null;
  }

  const outreachStatus = mapEngagementStatus(engagement.status);
  const outreachType = mapOutreachType(employerOutreachData.outreachType);

  if (!outreachStatus || !outreachType) {
    return null;
  }

  // Get employer name from job data or sender
  const employerName = engagement.job?.company || engagement.sender.name;

  return {
    outreachStatus,
    outreachType,
    employerName: employerName || undefined,
  };
};
